import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client/core';
import { intervalToDuration } from 'date-fns';
import { Observable, throwError as observableThrowError, combineLatest } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { FeatureFlags, FeatureFlagVariants } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { MODULE_DOB_SEX_PAGE } from '@app/core/mixpanel.constants';
import { StepName } from '@app/registration/enterprise/registration-step-name';
import { minimumAge } from '@app/shared/date-validator.directive';
import { ServiceArea } from '@app/shared/service-area';
import { ServiceAreaService } from '@app/shared/service-area.service';

import { WhitelistedEmployeeDependent } from '../__generated__/WhitelistedEmployeeDependent';
import { EnterpriseRegistration } from '../enterprise-registration';
import { EnterpriseRegistrationAnalyticsService } from '../enterprise-registration-analytics.service';
import { RegistrationStep } from '../registration-step';
import { WhitelistedEmployeeDependentGraphqlService } from '../whitelisted-employee-dependent-graphql.service';
import { DependentAlreadyRegisteredError, NoWhitelistedDependentFoundError } from '../whitelisted-employee-errors';
import { DobSexStepComponent } from './dob-sex-step.component';

@Injectable()
export class DobSexConfig extends RegistrationStep {
  GA_LABEL = 'Tell_More_About_Step';

  MODULE = MODULE_DOB_SEX_PAGE;
  component = DobSexStepComponent;
  progress = 40;
  showLoginLink = true;

  MINIMUM_AGE = 14;

  form: UntypedFormGroup = this.formBuilder.group({
    dob: this.formBuilder.control({ date: '' }, { validators: [minimumAge(this.MINIMUM_AGE)] }),
    genderDetails: '',
    sex: ['', Validators.required],
  });

  constructor(
    public serviceAreaService: ServiceAreaService,
    private enterpriseRegistrationAnalyticsService: EnterpriseRegistrationAnalyticsService,
    private formBuilder: UntypedFormBuilder,
    private whitelistDependentGraphQLService: WhitelistedEmployeeDependentGraphqlService,
    private launchDarklyService: LaunchDarklyService,
  ) {
    super();
  }

  initComponent(component: DobSexStepComponent, state: EnterpriseRegistration) {
    component.form = this.form;
    component.minimumAge = this.MINIMUM_AGE;
    component.b2bCompanyId = state.details.b2bCompanyId;
  }

  onDestroy() {
    this.form.controls.dob.clearValidators();
  }

  patchParams(params: Record<string, any>) {
    const { dob, gender, shouldDisableDob } = params;
    if (dob) {
      this.form.patchValue({ dob: { date: dob } });
    }
    if (gender) {
      this.form.patchValue({ sex: gender });
    }

    const dobControl = this.form.get('dob');
    if (shouldDisableDob) {
      this.launchDarklyService
        .featureFlag$(FeatureFlags.REMOTE_PRIMARY_CARE_ENABLED, FeatureFlagVariants.OFF)
        .subscribe(flag => {
          const isEnabled = flag !== FeatureFlagVariants.OFF;
          if (isEnabled) {
            dobControl.disable();
          }
        });
    }
  }

  submit(state: EnterpriseRegistration, captcha: any): Observable<any> {
    if (this.form.valid) {
      state.patient.gender = this.form.controls.sex.value;
      state.patient.genderDetails = this.form.controls.genderDetails.value;
      if (state.patient.genderDetails) {
        this.trackGenderSubmit(state);
      }
      state.patient.dob = this.form.controls.dob.value['date'];
      state.patient.ageInYears = intervalToDuration({ start: new Date(state.patient.dob), end: new Date() }).years;
      if (state.isWhitelisted && state.isAdultDependent && state.verifyDependentInfo()) {
        return captcha.getToken().pipe(
          switchMap((reCaptchaToken: string) =>
            this.whitelistDependentGraphQLService.fetch(state.dependentQueryParams(reCaptchaToken)),
          ),
          switchMap((result: ApolloQueryResult<WhitelistedEmployeeDependent>) => {
            const { whitelistedEmployeeDependent } = result.data;
            if (whitelistedEmployeeDependent === null) {
              return observableThrowError(new NoWhitelistedDependentFoundError());
            }
            if (whitelistedEmployeeDependent.registered) {
              return observableThrowError(new DependentAlreadyRegisteredError());
            }
            state.setCurrentStep(StepName.serviceArea);
            return combineLatest([
              this.serviceAreaService.getServiceAreas(),
              this.launchDarklyService.featureFlag$(FeatureFlags.REMOTE_PRIMARY_CARE_ENABLED, FeatureFlagVariants.OFF),
            ]).pipe(
              map(([serviceAreas, flag]) => {
                const isEnabled = flag !== FeatureFlagVariants.OFF;
                const virtualServiceArea = serviceAreas.find(area => area.name.includes('Virtual'));

                if (isEnabled && state.isRpcEligible && virtualServiceArea) {
                  state.setServiceArea(virtualServiceArea);
                  state.setCurrentStep(StepName.createAccount);
                }

                this.trackSubmission(state);
                return true;
              }),
            );
          }),
          catchError(err => observableThrowError(err)),
        );
      }

      if (state.shouldConvertAccount()) {
        return state.submitAccountConversion(captcha, state.b2bCompany.includesDependent);
      }

      const b2bCompanyId = state.details.b2bCompanyId;

      // This works for 99% of our use case for OM Now customers. We should refactor this
      // to have combineLatest with the above observable to include whitelisted and adult dependent
      // patients skipping the service area screen.
      this.serviceAreaService.getServiceAreas(b2bCompanyId);
      return this.serviceAreaService.serviceAreas$.pipe(
        switchMap((serviceAreas: ServiceArea[]) => {
          const virtualServiceArea = serviceAreas.find(area => area.name.includes('Virtual'));

          return this.launchDarklyService
            .featureFlag$(FeatureFlags.REMOTE_PRIMARY_CARE_ENABLED, FeatureFlagVariants.OFF)
            .pipe(
              map(flag => {
                const isEnabled = flag !== FeatureFlagVariants.OFF;
                if (isEnabled && state.isRpcEligible && virtualServiceArea) {
                  state.setServiceArea(virtualServiceArea);
                  state.setCurrentStep(StepName.createAccount);
                } else if (serviceAreas.length === 1) {
                  state.setServiceArea(serviceAreas[0]);
                  state.setCurrentStep(StepName.createAccount);
                } else {
                  state.setCurrentStep(StepName.serviceArea);
                }

                this.trackSubmission(state);
                return true;
              }),
            );
        }),
      );
    }
    return observableThrowError(new Error());
  }

  private trackSubmission(state: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.identifyAndUpdateUser(state.patient);

    this.enterpriseRegistrationAnalyticsService.regInputSubmitted({
      isWhitelist: state.isWhitelisted,
      module: this.MODULE,
      relationshipType: state.details.membershipType,
    });
  }

  private trackGenderSubmit(state: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.genderInformationAdded({
      module: this.MODULE,
      companyId: state.b2bCompanyId,
    });
  }
}
